import { CommonModule } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    linkedSignal,
    signal,
    untracked,
    viewChild,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { s25Resource } from "../../../util/s25.resource";
import { LoginStore } from "../../../stores/login.store";
import {
    ElectionApi,
    type ElectionDto,
    type ElectionPositionDto,
    type ElectionVoterRuleDto,
    type VoterRuleType,
} from "./election.api";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { DataIngestionService } from "../../data-ingestion/data.ingestion.service";
import { S25Util } from "../../../util/s25-util";
import { AlertService } from "../../../services/alert.service";
import { SeSecurityStore } from "../se.security.store";
import { RoleRights } from "../securityConfig/role.rights";
import { AccessLevels } from "../../../pojo/Fls";
import { S25RouterLinkDirective } from "../../s25-nav-bar/s25.router.link.directive";
import { S25RichTextEditorModule } from "../../s25-rich-text-editor/s25.rich.text.editor.module";
import { S25DatepickerModule } from "../../s25-datepicker/s25.datepicker.module";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { type DropDownItem } from "../../../pojo/DropDownItem";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";
import { SearchCriteriaService } from "../../../services/search/search-criteria/search.criteria.service";
import { OrganizationService } from "../../../services/organization.service";
import { S25TableModule } from "../../s25-table/s25.table.module";
import { Table } from "../../s25-table/Table";
import { GenericTableButtonComponent } from "../../s25-table/generics/generic.table.button.component";
import { GenericTableFadePreviewComponent } from "../../s25-table/generics/generic.table.fade.preview.component";
import { GenericTableRenderHTMLComponent } from "../../s25-table/generics/generic.table.render.html.component";
import { ContactService } from "../../../services/contact.service";
import { IngestionPointCategory } from "../../data-ingestion/data.ingestion.const";
import { S25FadingSuccessMsgComponent } from "../../s25-fading-success-msg/s25.fading.success.msg.component";
import { StateStore } from "../../../services/state/state.store";
import { ElectionAdminReturnStore } from "./election.admin.return.store";
import { S25TableComponent } from "../../s25-table/s25.table.component";
import { InstanceCount } from "../../../util/s25.instance.count";

type VoterRuleAddRowUi = { ruleType: VoterRuleType | null; roleDropDowns: DropDownItem[] };

@TypeManagerDecorator("s25-ng-election-admin-page")
@Component({
    selector: "s25-ng-election-admin-page",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        S25ButtonComponent,
        S25RouterLinkDirective,
        S25RichTextEditorModule,
        S25DatepickerModule,
        S25IconComponent,
        S25DropdownPaginatedModule,
        S25TableModule,
        S25FadingSuccessMsgComponent,
    ],
    host: { class: "election-admin-page" },
    template: `
        <div class="c-padding--double">
            <h1>Elections Admin</h1>
            @if (!login.loggedIn()) {
                <p>This page requires you to be logged in.</p>
            } @else if (!canViewElections()) {
                <p>You need election management access to configure elections.</p>
            } @else {
                @if (!isCreating() && !selectedElectionId()) {
                    <p class="election-admin__list-toolbar c-margin-bottom--single">
                        @if (canManageElectionsInstitutionWide()) {
                            <s25-ng-button [type]="'primary'" [onClick]="startCreate">New Election</s25-ng-button>
                        }
                        <s25-ng-button [type]="'outline'" [onClick]="refreshElectionList">Refresh</s25-ng-button>
                    </p>
                    @if (list.isLoading() || formPoints.isLoading()) {
                        <p>Loading…</p>
                    } @else if (list.error()) {
                        <p class="c-text--error">{{ $any(list.error())?.message || list.error() }}</p>
                    } @else {
                        <s25-ng-table [dataSource]="electionListTable()"></s25-ng-table>
                    }
                } @else {
                    <p class="c-margin-bottom--single election-admin__detail-toolbar">
                        <s25-ng-button [type]="'outline'" [onClick]="backToList">← Back to list</s25-ng-button>
                        @if (!isCreating() && selectedElectionId()) {
                            <s25-ng-button [type]="'outline'" [onClick]="refreshSelectedElectionDetail">
                                Refresh
                            </s25-ng-button>
                        }
                    </p>
                    @if (
                        !isCreating() &&
                        selectedElectionDetail.isLoading() &&
                        !selectedElectionDetail.value() &&
                        !selectedElectionDetail.lastValue()
                    ) {
                        <p>Loading…</p>
                    } @else if (electionDraft()) {
                        <h2 class="c-margin-top--double">
                            @if (isCreating()) {
                                Create Election
                            } @else if (canEditThisOrg()) {
                                Edit Election
                            } @else {
                                View Election
                            }
                        </h2>
                        @if (!isCreating() && !canEditThisOrg()) {
                            <p class="c-text--secondary c-margin-bottom--single">
                                You have view-only access to this election.
                            </p>
                        }
                        <form class="election-admin__form" (ngSubmit)="saveElection(); $event.preventDefault()">
                            <div class="form-group c-margin-bottom--single">
                                <label>Name *</label>
                                <input
                                    class="c-input"
                                    name="name"
                                    [ngModel]="electionDraft()!.name"
                                    (ngModelChange)="patchElectionDraft('name', $event)"
                                    [disabled]="!canEditThisOrg()"
                                    required
                                />
                            </div>
                            <div class="form-group c-margin-bottom--single">
                                <label>Election description (shown to voters and nominators)</label>
                                <s25-ng-rich-text-editor
                                    [modelValue]="electionDraft()!.descr"
                                    (modelValueChange)="patchElectionDraft('descr', $event)"
                                    [disabled]="!canEditThisOrg()"
                                ></s25-ng-rich-text-editor>
                            </div>
                            <div class="form-group c-margin-bottom--single">
                                <label>Nomination start</label>
                                <s25-datepicker
                                    [value]="electionDraft()!.nominationStartDt"
                                    (valueChange)="patchElectionDraftDate('nominationStartDt', $event)"
                                    [inline]="true"
                                    [showToday]="true"
                                    [autoSelectToday]="false"
                                    [readonly]="!canEditThisOrg()"
                                />
                            </div>
                            <div class="form-group c-margin-bottom--single">
                                <label>Nomination end</label>
                                <s25-datepicker
                                    [value]="electionDraft()!.nominationEndDt"
                                    (valueChange)="patchElectionDraftDate('nominationEndDt', $event)"
                                    [inline]="true"
                                    [showToday]="true"
                                    [autoSelectToday]="false"
                                    [readonly]="!canEditThisOrg()"
                                />
                            </div>
                            <div class="form-group c-margin-bottom--single">
                                <label>Voting start</label>
                                <s25-datepicker
                                    [value]="electionDraft()!.votingStartDt"
                                    (valueChange)="patchElectionDraftDate('votingStartDt', $event)"
                                    [inline]="true"
                                    [showToday]="true"
                                    [autoSelectToday]="false"
                                    [readonly]="!canEditThisOrg()"
                                />
                            </div>
                            <div class="form-group c-margin-bottom--single">
                                <label>Voting end</label>
                                <s25-datepicker
                                    [value]="electionDraft()!.votingEndDt"
                                    (valueChange)="patchElectionDraftDate('votingEndDt', $event)"
                                    [inline]="true"
                                    [showToday]="true"
                                    [autoSelectToday]="false"
                                    [readonly]="!canEditThisOrg()"
                                />
                            </div>
                            <div class="form-group c-margin-bottom--single election-admin__org-dropdown">
                                <label [id]="uniqueId">Organization</label>
                                <p
                                    class="c-text--secondary c-margin-bottom--half"
                                    style="font-size: 0.9em; max-width: 36rem"
                                >
                                    Leave unselected for a school-wide election.
                                </p>
                                <div class="election-admin__org-dropdown-row">
                                    <s25-organization-dropdown
                                        [value]="orgScopeDropdownValue()"
                                        (valueChange)="onOrgScopeValueChange($event)"
                                        [hasNoneItem]="true"
                                        [noneItemLabel]="'— School-wide —'"
                                        [readOnly]="!canEditThisOrg()"
                                        [blockDetailsNav]="true"
                                        [renderItem]="false"
                                        [ariaLabel]="{ by: uniqueId }"
                                    />
                                    @if (canEditThisOrg()) {
                                        <s25-ng-button
                                            [type]="'outline'"
                                            [onClick]="clearOrgScope"
                                            [disabled]="draftOrgAcctId() == null"
                                            class="election-admin__org-clear"
                                        >
                                            Clear
                                        </s25-ng-button>
                                    }
                                </div>
                            </div>
                            <div class="form-group c-margin-bottom--single">
                                <label>Candidate order</label>
                                <select
                                    class="c-input"
                                    name="candidateOrder"
                                    [ngModel]="electionDraft()!.candidateOrder"
                                    (ngModelChange)="patchElectionDraft('candidateOrder', $event)"
                                    [disabled]="!canEditThisOrg()"
                                >
                                    <option value="SORT_ORDER">Sort order</option>
                                    <option value="ALPHA">Alphabetical</option>
                                    <option value="RANDOM">Random</option>
                                </select>
                            </div>
                            <div class="form-group c-margin-bottom--single">
                                <label>Candidate nomination form</label>
                                <p class="c-text--secondary c-margin-bottom--half" style="font-size: 0.9em">
                                    Form nominees use during the nomination window (separate from the voting ballot).
                                </p>
                                <select
                                    class="c-input"
                                    name="candidateIngestionPointId"
                                    [ngModel]="electionDraft()!.candidateIngestionPointId"
                                    (ngModelChange)="patchElectionDraft('candidateIngestionPointId', $event)"
                                    [disabled]="!canEditThisOrg()"
                                >
                                    <option [ngValue]="null">— None —</option>
                                    @for (
                                        ingestionPoint of formPoints.value() || [];
                                        track ingestionPoint.ingestionPointId
                                    ) {
                                        <option [ngValue]="ingestionPoint.ingestionPointId">
                                            {{ ingestionPoint.ingestionPointName }}
                                        </option>
                                    }
                                </select>
                            </div>
                        </form>
                        @if (isCreating() && canEditThisOrg()) {
                            <p class="c-margin-top--double">
                                <s25-ng-button [type]="'primary'" [onClick]="saveElection">Save Election</s25-ng-button>
                            </p>
                        }
                        @if (!isCreating() && !!selectedElectionId()) {
                            <div class="c-margin-top--double c-margin-bottom--double">
                                @if (canEditThisOrg()) {
                                    <a
                                        s25-ng-router-link
                                        [stateName]="'seElectionManualCandidate'"
                                        [stateParams]="{ electionId: selectedElectionId()! }"
                                        class="c-margin-right--double"
                                    >
                                        Add candidates (manual)
                                    </a>
                                }
                                <a
                                    s25-ng-router-link
                                    [stateName]="'seElectionResults'"
                                    [stateParams]="{ electionId: selectedElectionId()! }"
                                >
                                    View results
                                </a>
                            </div>
                            <h2 class="c-margin-top--double">Candidates</h2>
                            <s25-ng-table
                                #candidatesTable
                                [caption]="'Candidates'"
                                [unlimitedWidth]="true"
                                [columnSortable]="false"
                                [dataSource]="candidatesReadonlyTable()"
                                [hasTotalRowCount]="true"
                                [hasRefresh]="false"
                                [showHeaderWhenNoData]="true"
                            />
                            <h2 class="c-margin-top--triple">Positions</h2>
                            <table class="c-table c-margin-bottom--double">
                                <thead>
                                    <tr>
                                        @if (canEditThisOrg()) {
                                            <th class="election-admin__order-col">Order</th>
                                        }
                                        <th>Title</th>
                                        @if (canEditThisOrg()) {
                                            <th>Actions</th>
                                        }
                                    </tr>
                                </thead>
                                <tbody>
                                    @for (
                                        position of positionRows();
                                        track position.electionPositionId;
                                        let positionIndex = $index
                                    ) {
                                        <tr>
                                            @if (canEditThisOrg()) {
                                                <td class="election-admin__order-col">
                                                    <div class="election-admin__order-cell">
                                                        <s25-ng-button
                                                            [type]="'flat'"
                                                            [onClick]="movePositionFactory(position, -1)"
                                                            [ariaLabel]="
                                                                'Move ' + position.positionTitle + ' earlier in list'
                                                            "
                                                            [disabled]="positionIndex === 0 || movingPositions()"
                                                        >
                                                            <s25-ng-icon [type]="'chevronUp'" [ariaLabel]="false" />
                                                        </s25-ng-button>
                                                        <s25-ng-button
                                                            [type]="'flat'"
                                                            [onClick]="movePositionFactory(position, 1)"
                                                            [ariaLabel]="
                                                                'Move ' + position.positionTitle + ' later in list'
                                                            "
                                                            [disabled]="
                                                                positionIndex === positionRows().length - 1 ||
                                                                movingPositions()
                                                            "
                                                        >
                                                            <s25-ng-icon [type]="'chevronDown'" [ariaLabel]="false" />
                                                        </s25-ng-button>
                                                    </div>
                                                </td>
                                            }
                                            <td>{{ position.positionTitle }}</td>
                                            @if (canEditThisOrg()) {
                                                <td>
                                                    <div class="election-admin__position-actions">
                                                        <s25-ng-button
                                                            [type]="'outline'"
                                                            [buttonClass]="'aw-button--primary'"
                                                            [onClick]="editPositionRowFactory(position)"
                                                        >
                                                            Edit
                                                        </s25-ng-button>
                                                        <s25-ng-button
                                                            [type]="'danger--outline'"
                                                            [onClick]="removePositionRowFactory(position)"
                                                        >
                                                            Delete
                                                        </s25-ng-button>
                                                    </div>
                                                </td>
                                            }
                                        </tr>
                                    }
                                </tbody>
                            </table>
                            @if (canEditThisOrg() && (editingPos() || newPositionMode())) {
                                <div
                                    class="election-admin__labeled-fields c-border c-padding--double c-margin-bottom--double"
                                >
                                    <h3>{{ newPositionMode() ? "Add position" : "Edit position" }}</h3>
                                    <div class="form-group c-margin-bottom--single">
                                        <label>Title</label>
                                        <input
                                            class="c-input"
                                            name="pTitle"
                                            [ngModel]="posFormTitle()"
                                            (ngModelChange)="posFormTitle.set($event)"
                                        />
                                    </div>
                                    <div class="form-group c-margin-bottom--single">
                                        <label>Description</label>
                                        <s25-ng-rich-text-editor
                                            [modelValue]="posFormDesc()"
                                            (modelValueChange)="posFormDesc.set($event ?? '')"
                                        ></s25-ng-rich-text-editor>
                                    </div>
                                    <s25-ng-button [type]="'primary'" [onClick]="savePosForm">
                                        {{ newPositionMode() ? "Add" : "Save" }}
                                    </s25-ng-button>
                                    <s25-ng-button
                                        [type]="'outline'"
                                        [onClick]="cancelPosEdit"
                                        class="c-margin-left--half"
                                    >
                                        Cancel
                                    </s25-ng-button>
                                </div>
                            } @else if (canEditThisOrg()) {
                                <p class="c-margin-bottom--double">
                                    <s25-ng-button [type]="'primary'" [onClick]="startAddPosition">
                                        Add Position
                                    </s25-ng-button>
                                </p>
                            }
                            @if (positionRows().length) {
                                <h2>Positions: Voter Rules</h2>
                                <p class="c-text--secondary c-margin-bottom--single">
                                    Configure who may vote for each position.
                                    <strong>ALL</strong> means any authenticated user may vote.
                                    <strong>MEMBERS</strong> means only members of the organization may vote.
                                    <strong>ROLE</strong> means only members with the selected role may vote.
                                </p>
                                @for (position of positionRows(); track position.electionPositionId) {
                                    <section
                                        class="election-admin__position-voter-rules"
                                        [attr.aria-label]="position.positionTitle + ' voter rules'"
                                    >
                                        <h3 class="election-admin__position-voter-rules-title">
                                            {{ position.positionTitle }}
                                        </h3>
                                        <div class="election-admin__position-voter-rules-body">
                                            @if (rulesFor(position).length) {
                                                <ul
                                                    class="election-admin__voter-rules"
                                                    aria-label="Current voter rules"
                                                >
                                                    @for (
                                                        rule of rulesFor(position);
                                                        track rule.electionPositionVoterRuleId
                                                    ) {
                                                        <li class="election-admin__voter-rule">
                                                            <span class="election-admin__voter-rule-text">
                                                                <span class="election-admin__voter-rule-type">{{
                                                                    rule.ruleType
                                                                }}</span>
                                                                @if (rule.ruleType === "ROLE" && rule.roleId) {
                                                                    <span class="c-text--secondary">
                                                                        ({{ voterRuleRoleDisplayName(rule) }})
                                                                    </span>
                                                                }
                                                            </span>
                                                            @if (canEditThisOrg()) {
                                                                <s25-ng-button
                                                                    class="election-admin__voter-rule-remove"
                                                                    [type]="'danger--outline'"
                                                                    [onClick]="deleteRule"
                                                                    [onClickArgs]="[rule.electionPositionVoterRuleId!]"
                                                                    [title]="'Remove'"
                                                                >
                                                                    Remove
                                                                </s25-ng-button>
                                                            }
                                                        </li>
                                                    }
                                                </ul>
                                            } @else {
                                                <p class="election-admin__voter-rules-empty">No voter rules yet.</p>
                                            }
                                        </div>
                                        @if (canEditThisOrg()) {
                                            <section class="election-admin__add-voter-rule" aria-label="Add New Rule">
                                                <p class="election-admin__add-voter-rule-label">Add New Rule</p>
                                                <div class="election-admin__add-voter-rule-controls">
                                                    <select
                                                        class="c-input election-admin__rule-type-select"
                                                        [ngModel]="getRuleTypeFor(position.electionPositionId!)"
                                                        (ngModelChange)="
                                                            onRuleTypeSelect(position.electionPositionId!, $event)
                                                        "
                                                    >
                                                        <option [ngValue]="null">Choose Rule Type</option>
                                                        <option value="ALL">ALL</option>
                                                        <option value="MEMBERS">MEMBERS</option>
                                                        <option value="ROLE">ROLE</option>
                                                    </select>
                                                    @if (getRuleTypeFor(position.electionPositionId!) === "ROLE") {
                                                        <s25-ng-dropdown-multi-search-criteria
                                                            class="election-admin__role-dropdown"
                                                            [type]="'organizationRoles'"
                                                            [value]="
                                                                getRuleRoleDropDownsFor(position.electionPositionId!)
                                                            "
                                                            (valueChange)="
                                                                onRuleRoleDropDownsChange(
                                                                    position.electionPositionId!,
                                                                    $event
                                                                )
                                                            "
                                                            [placeholder]="'Select Roles'"
                                                            [hasDone]="false"
                                                            [ariaLabel]="'Select organization roles'"
                                                        />
                                                    }
                                                    <s25-ng-button
                                                        [type]="'outline'"
                                                        [buttonClass]="'aw-button--primary'"
                                                        [onClick]="addRuleFactory(position.electionPositionId!)"
                                                        [disabled]="!canAddVoterRule(position.electionPositionId!)"
                                                    >
                                                        Add Voting Rule
                                                    </s25-ng-button>
                                                </div>
                                            </section>
                                        }
                                    </section>
                                }
                            }
                            @if (canEditThisOrg()) {
                                <p class="c-margin-top--double election-admin__save-row">
                                    <s25-ng-button [type]="'primary'" [onClick]="saveElection"
                                        >Save Election</s25-ng-button
                                    >
                                    <s25-ng-fading-success-msg [(message)]="saveSuccessMessage" />
                                </p>
                                <p class="c-margin-top--double">
                                    <s25-ng-button [type]="'danger--outline'" [onClick]="deleteThisElection">
                                        Delete this Election
                                    </s25-ng-button>
                                </p>
                            }
                        }
                    }
                }
            }
        </div>
    `,
    styles: `
        .election-admin__save-row {
            display: flex;
            align-items: center;
            gap: 1rem;
        }

        .election-admin__detail-toolbar {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 0.75rem;
        }
        .election-admin__list-toolbar {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 0.75rem 1.25rem;
        }
        .election-admin__form .form-group,
        .election-admin__labeled-fields .form-group {
            display: grid;
            grid-template-columns: minmax(10.5rem, 16rem) minmax(0, 1fr);
            column-gap: 1.25rem;
            row-gap: 0.65rem;
            align-items: start;
            max-width: 48rem;
        }
        .election-admin__form .form-group > label,
        .election-admin__labeled-fields .form-group > label {
            grid-column: 1;
            grid-row: 1 / -1;
            margin: 0;
            padding-top: 0.35rem;
        }
        .election-admin__form .form-group > :not(label),
        .election-admin__labeled-fields .form-group > :not(label) {
            grid-column: 2;
            min-width: 0;
        }
        .election-admin__form .election-admin__org-dropdown > p {
            margin-top: 0;
        }
        .election-admin__org-dropdown-row {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 0.75rem;
            max-width: 40rem;
        }
        .election-admin__org-dropdown-row s25-organization-dropdown {
            display: block;
            flex: 1 1 16rem;
            min-width: 0;
        }
        .election-admin__org-clear {
            flex: 0 0 auto;
        }
        .c-table {
            width: 100%;
            border-collapse: collapse;
        }
        .c-table th,
        .c-table td {
            border: 1px solid #ddd;
            padding: 0.4rem 0.6rem;
        }
        .election-admin__role-dropdown {
            display: block;
            min-width: 14rem;
            max-width: 22rem;
        }
        :host ::ng-deep .election-admin__add-voter-rule-controls .election-admin__role-dropdown .ngDropdownPaginated {
            min-width: 14rem;
            max-width: 22rem;
            padding: 2px;
            border: 1px solid var(--color--border-bold) !important;
            border-radius: 6px;
            background: var(--color--form-bg);
        }
        :host
            ::ng-deep
            .election-admin__add-voter-rule-controls
            .election-admin__role-dropdown
            .ngDropdownPaginated
            .select2-container
            .select2-choice.ui-select-match {
            min-height: 2.125rem;
            padding-inline: 0.5rem 2em;
            display: flex;
            align-items: center;
        }
        .election-admin__position-voter-rules {
            border: 1px solid var(--color--border);
            border-radius: 4px;
            padding: 1rem 1.25rem 1.25rem;
            margin-bottom: 1.25rem;
            background: var(--color--surface-bg);
        }
        .election-admin__position-voter-rules-title {
            margin: 0 0 0.75rem;
            padding-bottom: 0.5rem;
            border-bottom: 1px solid var(--color--border);
        }
        .election-admin__position-voter-rules-body {
            min-height: 0.5rem;
        }
        .election-admin__rule-type-select {
            box-sizing: border-box;
            min-width: 12rem;
            height: 2.125rem;
            padding: 0 2rem 0 0.75rem;
            line-height: 2.125rem;
            border-radius: 6px;
        }
        .election-admin__order-col {
            width: 5.5rem;
            white-space: nowrap;
        }
        .election-admin__order-cell {
            display: flex;
            align-items: center;
            gap: 0.25rem;
        }
        .election-admin__position-actions {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            column-gap: 0.75rem;
            row-gap: 0.35rem;
        }
        .election-admin__voter-rules {
            list-style: none;
            margin: 0 0 0.75rem 0;
            padding: 0;
        }
        .election-admin__voter-rule {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 0.5rem 0.75rem;
            padding: 0.45rem 0;
            border-bottom: 1px solid #e8e8e8;
        }
        .election-admin__voter-rule:last-child {
            border-bottom: none;
        }
        .election-admin__voter-rule-text {
            display: inline-flex;
            flex-wrap: wrap;
            align-items: baseline;
            gap: 0.35rem 0.5rem;
            min-width: 0;
        }
        .election-admin__voter-rule-type {
            font-weight: 600;
        }
        .election-admin__voter-rule-remove {
            flex-shrink: 0;
        }
        .election-admin__add-rule-row {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            column-gap: 1rem;
            row-gap: 0.65rem;
            margin-top: 0.25rem;
        }
        .election-admin__voter-rules-empty {
            margin: 0.25rem 0 0.75rem;
            color: var(--color--text-muted);
        }
        .election-admin__add-voter-rule {
            margin-top: 0.75rem;
            padding: 0.85rem 1rem;
            background-color: rgba(var(--cn-blue-rgb), 0.08);
            border-radius: 4px;
        }
        .election-admin__add-voter-rule-label {
            margin: 0 0 0.5rem;
            font-weight: 600;
        }
        .election-admin__add-voter-rule-controls {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            column-gap: 1rem;
            row-gap: 0.65rem;
        }
        :host ::ng-deep .election-admin__add-voter-rule-controls s25-ng-button {
            display: inline-flex;
            align-items: center;
        }
    `,
})
export class ElectionAdminPageComponent {
    private static nextId = InstanceCount.idGenerator({ prefix: "ElectionAdminPage" });

    readonly login = inject(LoginStore);
    private readonly seSec = inject(SeSecurityStore);
    private readonly stateStore = inject(StateStore);
    private readonly electionAdminReturn = inject(ElectionAdminReturnStore);

    private readonly candidatesTable = viewChild<S25TableComponent>("candidatesTable");

    protected readonly uniqueId = ElectionAdminPageComponent.nextId();

    constructor() {
        effect(() => {
            if (this.stateStore.currentSeState() !== "seElectionAdmin") {
                return;
            }
            const electionId = this.electionAdminReturn.pendingReturnToElection();
            if (electionId == null) {
                return;
            }
            untracked(() => {
                this.electionAdminReturn.clearReturnToElection();
                this.openEdit(electionId);
                void this.reloadSelectedElectionDetailAndCandidatesTable();
            });
        });
    }

    readonly isCreating = signal(false);
    readonly selectedElectionId = signal<number | null>(null);
    private readonly listGen = signal(0);
    private readonly formPointsGen = signal(0);
    private readonly selectedElectionDetailGen = signal(0);

    readonly canViewElections = this.seSec.canViewElections;
    readonly canManageElectionsInstitutionWide = this.seSec.canManageElectionsInstitutionWide;

    readonly list = s25Resource({
        if: () => this.login.loggedIn() && this.login.init() && this.canViewElections(),
        params: () => this.listGen(),
        loader: () => ElectionApi.listElections(),
    });

    readonly formPoints = s25Resource({
        if: () => this.canViewElections(),
        params: () => this.formPointsGen(),
        loader: () =>
            DataIngestionService.getIngestionPoints(IngestionPointCategory.ENGAGEMENT, undefined, false, [
                "electionCandidate",
            ]),
    });

    /**
     * Elections list: name, linked nomination form label (when list row includes {@link ElectionDto#candidateIngestionPointId}), edit, delete.
     */
    readonly electionListTable = computed((): Table.DataSource => {
        void this.listGen();
        void this.formPointsGen();
        const canManageList = this.canManageElectionsInstitutionWide();
        const elections = this.list.value() ?? [];
        const formNameByIngestionPointId = new Map<number, string>();
        for (const ingestionPoint of this.formPoints.value() ?? []) {
            const ingestionPointId = ingestionPoint.ingestionPointId;
            if (ingestionPointId != null && !Number.isNaN(Number(ingestionPointId))) {
                formNameByIngestionPointId.set(Number(ingestionPointId), ingestionPoint.ingestionPointName ?? "");
            }
        }
        const rows: Table.Row[] = elections
            .filter((election) => election.electionId != null)
            .map((election) => {
                const electionId = election.electionId!;
                const candidateIngestionPointId = election.candidateIngestionPointId;
                const formLabel =
                    candidateIngestionPointId != null
                        ? (formNameByIngestionPointId.get(candidateIngestionPointId) ?? "—")
                        : "—";
                const cells: Table.Row["cells"] = {
                    name: { text: election.name ?? "" },
                    nominationForm: { text: formLabel },
                    edit: {
                        component: GenericTableButtonComponent,
                        inputs: {
                            label: canManageList ? "Edit" : "View",
                            type: "outline",
                            onClick: async () => {
                                this.openEdit(electionId);
                            },
                        },
                    } satisfies Table.Cell<GenericTableButtonComponent>,
                };
                if (canManageList) {
                    cells.delete = {
                        component: GenericTableButtonComponent,
                        inputs: {
                            label: "Delete",
                            type: "danger--outline",
                            onClick: async () => {
                                await this.deleteElectionFromListRow(election);
                            },
                        },
                    } satisfies Table.Cell<GenericTableButtonComponent>;
                    cells.copy = {
                        component: GenericTableButtonComponent,
                        inputs: {
                            label: "Copy",
                            type: "outline",
                            onClick: async () => {
                                await this.copyElectionFromListRow(electionId);
                            },
                        },
                    } satisfies Table.Cell<GenericTableButtonComponent>;
                }
                return {
                    id: electionId,
                    name: election.name ?? "",
                    cells,
                };
            });
        const columns: Table.Column[] = [
            { id: "name", header: "Election" },
            { id: "nominationForm", header: "Nomination form" },
            {
                id: "edit",
                header: canManageList ? "Edit" : "View",
                width: "min-content",
                sortable: false,
                export: false,
            },
        ];
        if (canManageList) {
            columns.push(
                { id: "copy", header: "Copy", width: "min-content", sortable: false, export: false },
                { id: "delete", header: "Delete", width: "min-content", sortable: false, export: false },
            );
        }
        return {
            type: "unpaginated",
            dataSource: () => Promise.resolve({ rows }),
            columns,
        };
    });

    readonly selectedElectionDetail = s25Resource({
        if: () => !this.isCreating() && this.selectedElectionId() != null,
        params: () => ({ electionId: this.selectedElectionId()!, g: this.selectedElectionDetailGen() }),
        loader: async ({ params: { electionId } }) => {
            const election = await ElectionApi.getElection(electionId);
            return { election };
        },
    });

    readonly electionDraft = linkedSignal<ElectionDto | null>(() => {
        if (this.isCreating()) {
            return blankElectionDraft();
        }
        if (!this.selectedElectionId()) {
            return null;
        }
        const loadedElection =
            this.selectedElectionDetail.value()?.election ?? this.selectedElectionDetail.lastValue()?.election;
        return loadedElection ? normalizeElectionDtoDateTimes({ ...loadedElection }) : null;
    });

    readonly draftOrgAcctId = computed((): number | null => this.electionDraft()?.acctId ?? null);

    private readonly orgEditPermResource = s25Resource({
        if: () => this.draftOrgAcctId() != null,
        params: () => this.draftOrgAcctId(),
        loader: async ({ params: acctId }) => {
            await this.seSec.ensureInitialized();
            return this.seSec.hasMinAccessForOrg(
                RoleRights.RoleRightsList.SE_ELECTION_MANAGE,
                acctId!,
                AccessLevels.Full,
            );
        },
    });

    readonly canEditThisOrg = computed((): boolean => {
        if (this.isCreating()) {
            const draftAcctId = this.electionDraft()?.acctId;
            if (draftAcctId == null) {
                return this.canManageElectionsInstitutionWide();
            }
            return this.resolveOrgEditPermission();
        }
        if (this.draftOrgAcctId() == null) {
            return this.canManageElectionsInstitutionWide();
        }
        return this.resolveOrgEditPermission();
    });

    private resolveOrgEditPermission(): boolean {
        if (this.orgEditPermResource.isLoading()) {
            const previousValue = this.orgEditPermResource.lastValue();
            if (previousValue !== undefined) {
                return previousValue;
            }
            if (this.isCreating()) {
                return this.canManageElectionsInstitutionWide();
            }
            return false;
        }
        return this.orgEditPermResource.value() ?? false;
    }

    private readonly orgScopeResource = s25Resource({
        if: () => this.draftOrgAcctId() != null,
        params: () => this.draftOrgAcctId(),
        loader: async ({ params: acctId }) => {
            if (acctId == null) {
                return null;
            }
            const organizations = await OrganizationService.getOrganizationsById([acctId], []);
            return organizations?.[0] ?? null;
        },
    });

    readonly orgScopeDropdownValue = linkedSignal<
        { acctId: number | null; orgName: string | null },
        DropDownItem | null
    >({
        source: () => ({
            acctId: this.draftOrgAcctId(),
            orgName: this.orgScopeResource.value()?.organization_name ?? null,
        }),
        computation: (source, previous) => {
            if (source.acctId == null) {
                return null;
            }
            const itemName = source.orgName ?? previous?.value?.itemName ?? "";
            return {
                itemId: source.acctId,
                itemTypeId: 2,
                itemName,
            } as DropDownItem;
        },
    });

    private readonly orgRolesResource = s25Resource({
        if: () => this.canViewElections(),
        loader: () => SearchCriteriaService.getOrganizationRoles(true, false),
    });

    private readonly orgRoleNameById = computed((): Map<number, string> => {
        const rows = (this.orgRolesResource.value() ?? []) as Array<{
            itemId?: string | number;
            itemName?: string;
            txt?: string;
        }>;
        const roleNameByRoleId = new Map<number, string>();
        for (const roleRow of rows) {
            const roleId = Number(roleRow.itemId);
            const roleName = roleRow.itemName ?? roleRow.txt;
            if (Number.isNaN(roleId) || roleName == null || roleName === "") {
                continue;
            }
            roleNameByRoleId.set(roleId, String(roleName));
        }
        return roleNameByRoleId;
    });

    readonly newPositionMode = signal(false);
    readonly editingPos = signal<ElectionPositionDto | null>(null);

    readonly posFormTitle = linkedSignal<string>(() => this.editingPos()?.positionTitle ?? "");
    readonly posFormDesc = linkedSignal<string>(() => this.editingPos()?.descr ?? "");

    readonly positionRows = linkedSignal<ElectionPositionDto[]>(() => {
        const electionDetail =
            this.selectedElectionDetail.value()?.election ?? this.selectedElectionDetail.lastValue()?.election;
        const positions = electionDetail?.positions ?? [];
        return [...positions].sort((left, right) => (left.sortOrder ?? 0) - (right.sortOrder ?? 0));
    });

    /**
     * Read-only roster: position title, resolved candidate name, rich campaign brief (fade/expand in cell).
     */
    readonly candidatesReadonlyTable = computed((): Table.DataSource => {
        const election =
            this.selectedElectionDetail.value()?.election ?? this.selectedElectionDetail.lastValue()?.election;
        return {
            type: "unpaginated",
            dataSource: async () => {
                const contIds = this.collectCandidateContactIds(election);
                const nameByContactId = await this.loadCandidateNameByContactId(contIds);
                const rows = this.buildCandidateTableRows(election, nameByContactId);
                return { rows, totalRows: rows.length };
            },
            columns: [
                { id: "positionTitle", header: "Position" },
                { id: "candidateName", header: "Candidate" },
                { id: "brief", header: "Campaign brief" },
            ],
        };
    });

    private buildCandidateTableRows(
        election: ElectionDto | null | undefined,
        nameByContactId: Map<number, string>,
    ): Table.Row[] {
        const resultRows: Table.Row[] = [];
        const electionPositions = election?.positions;
        if (!electionPositions?.length) {
            return resultRows;
        }
        const sortedPositions = [...electionPositions].sort(
            (left, right) => (left.sortOrder ?? 0) - (right.sortOrder ?? 0),
        );
        for (const position of sortedPositions) {
            const positionTitle = position.positionTitle ?? "";
            const positionCandidates = [...(position.candidates ?? [])].sort(
                (left, right) => (left.sortOrder ?? 0) - (right.sortOrder ?? 0),
            );
            for (const candidate of positionCandidates) {
                const contactId = Number(candidate.contId);
                if (Number.isNaN(contactId)) {
                    continue;
                }
                const displayName = nameByContactId.get(contactId) || `Contact ${contactId}`;
                const brief = S25Util.toStr(candidate.campaignBrief);
                const rowId =
                    candidate.electionCandidateId != null
                        ? String(candidate.electionCandidateId)
                        : `${position.electionPositionId ?? "position"}-${contactId}`;
                const briefPlainForFilter = brief
                    .replace(/<[^>]+>/g, " ")
                    .replace(/\s+/g, " ")
                    .trim();
                const briefCell: Table.Cell = brief
                    ? {
                          component: GenericTableFadePreviewComponent,
                          inputs: {
                              maxHeight: 100,
                              fadeHeight: 36,
                              cell: {
                                  component: GenericTableRenderHTMLComponent,
                                  inputs: { text: brief, isHtml: true },
                              },
                          },
                          textValue: briefPlainForFilter,
                      }
                    : { text: "—" };
                resultRows.push({
                    id: rowId,
                    name: `${positionTitle} ${displayName}`.trim(),
                    cells: {
                        positionTitle: { text: positionTitle },
                        candidateName: { text: displayName },
                        brief: briefCell,
                    },
                });
            }
        }
        return resultRows;
    }

    private collectCandidateContactIds(election: ElectionDto | null | undefined): number[] {
        const contactIdSet = new Set<number>();
        for (const position of election?.positions ?? []) {
            for (const candidate of position.candidates ?? []) {
                if (S25Util.isUndefined(candidate.contId)) {
                    continue;
                }
                const contactId = Number(candidate.contId);
                if (!Number.isFinite(contactId)) {
                    continue;
                }
                contactIdSet.add(contactId);
            }
        }
        return [...contactIdSet].sort((left, right) => left - right);
    }

    private async loadCandidateNameByContactId(contactIds: number[]): Promise<Map<number, string>> {
        if (!contactIds.length) {
            return new Map();
        }
        const items = await ContactService.getContactsNameAndFormal(contactIds);
        return new Map(
            items.map((item) => {
                const contactId = Number(item.itemId);
                return [contactId, S25Util.toStr(item.itemName)] as [number, string];
            }),
        );
    }

    readonly movingPositions = signal(false);
    readonly saveSuccessMessage = signal<string>("");

    private readonly addRuleUiByElectionPositionId = signal<Record<number, VoterRuleAddRowUi>>({});

    getRuleTypeFor(electionPositionId: number): VoterRuleType | null {
        return this.addRuleUiByElectionPositionId()[electionPositionId]?.ruleType ?? null;
    }

    canAddVoterRule(electionPositionId: number): boolean {
        const rowUi = this.addRuleUiByElectionPositionId()[electionPositionId];
        const ruleType = rowUi?.ruleType;
        if (!ruleType) {
            return false;
        }
        if (ruleType === "ROLE") {
            return (rowUi?.roleDropDowns?.length ?? 0) > 0;
        }
        return true;
    }

    onRuleTypeSelect(electionPositionId: number, nextRuleType: VoterRuleType | null) {
        this.addRuleUiByElectionPositionId.update((previous) => ({
            ...previous,
            [electionPositionId]: {
                ruleType: nextRuleType,
                roleDropDowns: nextRuleType === "ROLE" ? (previous[electionPositionId]?.roleDropDowns ?? []) : [],
            },
        }));
    }

    getRuleRoleDropDownsFor(electionPositionId: number): DropDownItem[] {
        return this.addRuleUiByElectionPositionId()[electionPositionId]?.roleDropDowns ?? [];
    }

    onRuleRoleDropDownsChange(electionPositionId: number, items: DropDownItem[] | null | undefined) {
        this.addRuleUiByElectionPositionId.update((previous) => ({
            ...previous,
            [electionPositionId]: {
                ruleType: previous[electionPositionId]?.ruleType ?? null,
                roleDropDowns: items ?? [],
            },
        }));
    }

    addRuleFactory(electionPositionId: number) {
        return () => {
            void this.addRule(electionPositionId);
        };
    }

    voterRuleRoleDisplayName(rule: ElectionVoterRuleDto): string {
        if (rule.ruleType !== "ROLE" || !rule.roleId) {
            return "";
        }
        const resolvedName = this.orgRoleNameById().get(rule.roleId);
        return resolvedName ?? "Unknown organization role";
    }

    clearOrgScope = () => {
        this.onOrgScopeValueChange(null);
    };

    onOrgScopeValueChange = (item: DropDownItem | null | undefined) => {
        if (!item) {
            this.orgScopeDropdownValue.set(null);
            this.patchElectionDraft("acctId", null);
            return;
        }
        if ("isNoneItem" in item && item.isNoneItem) {
            this.orgScopeDropdownValue.set(null);
            this.patchElectionDraft("acctId", null);
            return;
        }
        this.orgScopeDropdownValue.set(item);
        const acctId = item.itemId && !isNaN(Number(item.itemId)) ? Number(item.itemId) : null;
        this.patchElectionDraft("acctId", acctId);
    };

    editPosition(position: ElectionPositionDto) {
        if (!this.guardEditPermission()) {
            return;
        }
        this.newPositionMode.set(false);
        this.editingPos.set(position);
    }

    openEdit(electionId: number, successMessage?: string) {
        this.isCreating.set(false);
        this.selectedElectionId.set(electionId);
        this.newPositionMode.set(false);
        this.editingPos.set(null);
        this.saveSuccessMessage.set(successMessage ?? "");
        this.selectedElectionDetailGen.update((count) => count + 1);
    }

    startCreate = () => {
        if (!this.canManageElectionsInstitutionWide()) {
            void AlertService.alert("You do not have permission to create elections.");
            return;
        }
        this.isCreating.set(true);
        this.selectedElectionId.set(null);
        this.newPositionMode.set(false);
        this.editingPos.set(null);
        this.saveSuccessMessage.set("");
    };

    refreshElectionList = () => {
        this.listGen.update((count) => count + 1);
        this.formPointsGen.update((count) => count + 1);
    };

    refreshSelectedElectionDetail = async () => {
        if (this.isCreating() || this.selectedElectionId() == null) {
            return;
        }
        this.selectedElectionDetailGen.update((count) => count + 1);
        await this.reloadSelectedElectionDetailAndCandidatesTable();
    };

    private reloadSelectedElectionDetailAndCandidatesTable = async () => {
        await this.selectedElectionDetail.reloadAndAwait();
        await this.candidatesTable()?.refresh(true, true, false);
    };

    backToList = () => {
        this.isCreating.set(false);
        this.selectedElectionId.set(null);
        this.saveSuccessMessage.set("");
    };

    patchElectionDraftDate(
        key: "nominationStartDt" | "nominationEndDt" | "votingStartDt" | "votingEndDt",
        nextDate: Date | null | undefined,
    ) {
        if (nextDate == null) {
            return;
        }
        const endOfDay = key === "nominationEndDt" || key === "votingEndDt";
        this.patchElectionDraft(key, formatElectionDayBoundaryForApi(nextDate, endOfDay));
    }

    patchElectionDraft<K extends keyof ElectionDto>(key: K, value: ElectionDto[K]) {
        const currentElectionDraft = this.electionDraft();
        if (!currentElectionDraft) {
            return;
        }
        this.electionDraft.set({ ...currentElectionDraft, [key]: value });
    }

    movePositionFactory = (position: ElectionPositionDto, direction: -1 | 1) => {
        return () => this.runMovePosition(position, direction);
    };

    editPositionRowFactory = (position: ElectionPositionDto) => {
        return () => {
            this.editPosition(position);
        };
    };

    removePositionRowFactory = (position: ElectionPositionDto) => {
        return () => this.removePosition(position);
    };

    private async denyEditPermission(): Promise<void> {
        await AlertService.alert("You do not have permission to edit this election.");
    }

    private guardEditPermission(): boolean {
        if (!this.canEditThisOrg()) {
            void this.denyEditPermission();
            return false;
        }
        return true;
    }

    private async runMovePosition(position: ElectionPositionDto, direction: -1 | 1): Promise<void> {
        if (!this.guardEditPermission()) {
            return;
        }
        const electionId = this.selectedElectionId();
        if (!electionId || !position.electionPositionId || this.movingPositions()) {
            return;
        }
        const rows = this.positionRows();
        const currentIndex = rows.findIndex((row) => row.electionPositionId === position.electionPositionId);
        const swapIndex = currentIndex + direction;
        if (currentIndex < 0 || swapIndex < 0 || swapIndex >= rows.length) {
            return;
        }
        const swapWith = rows[swapIndex];
        if (!swapWith.electionPositionId) {
            return;
        }
        const positionSort = position.sortOrder ?? 0;
        const swapSort = swapWith.sortOrder ?? 0;

        // optimistic that the update via API will succeed
        const optimisticRows = rows
            .map((row) => {
                if (row.electionPositionId === position.electionPositionId) {
                    return { ...row, sortOrder: swapSort };
                }
                if (row.electionPositionId === swapWith.electionPositionId) {
                    return { ...row, sortOrder: positionSort };
                }
                return row;
            })
            .sort((left, right) => (left.sortOrder ?? 0) - (right.sortOrder ?? 0));
        this.positionRows.set(optimisticRows);

        this.movingPositions.set(true);
        try {
            await Promise.all([
                ElectionApi.updatePosition(electionId, position.electionPositionId, {
                    positionTitle: position.positionTitle,
                    descr: position.descr,
                    sortOrder: swapSort,
                }),
                ElectionApi.updatePosition(electionId, swapWith.electionPositionId, {
                    positionTitle: swapWith.positionTitle,
                    descr: swapWith.descr,
                    sortOrder: positionSort,
                }),
            ]);
        } catch (caughtError) {
            await AlertService.alert(S25Util.errorText(caughtError));
        } finally {
            this.movingPositions.set(false);
            this.selectedElectionDetailGen.update((count) => count + 1);
        }
    }

    rulesFor(position: ElectionPositionDto) {
        return position.voterRules ?? [];
    }

    savePosForm = () => {
        if (this.newPositionMode()) {
            void this.addPosition();
        } else {
            void this.updatePosition();
        }
    };

    saveElection = async () => {
        const currentElectionDraft = this.electionDraft();
        if (!currentElectionDraft) {
            return;
        }
        if (!this.guardEditPermission()) {
            return;
        }
        const payload = normalizeElectionDtoDateTimes(currentElectionDraft);
        try {
            if (this.isCreating()) {
                const created = await ElectionApi.createElection(payload);
                this.isCreating.set(false);
                this.listGen.update((count) => count + 1);
                if (!!created.electionId) {
                    this.openEdit(created.electionId);
                } else {
                    this.backToList();
                }
            } else {
                const electionId = this.selectedElectionId()!;
                await ElectionApi.updateElection(electionId, payload);
                this.listGen.update((count) => count + 1);
                this.selectedElectionDetailGen.update((count) => count + 1);
                this.saveSuccessMessage.set("Election saved successfully.");
            }
        } catch (caughtError) {
            await AlertService.alert(S25Util.errorText(caughtError));
        }
    };

    startAddPosition = () => {
        if (!this.guardEditPermission()) {
            return;
        }
        this.newPositionMode.set(true);
        this.editingPos.set(null);
        this.posFormTitle.set("");
        this.posFormDesc.set("");
    };

    cancelPosEdit = () => {
        this.newPositionMode.set(false);
        this.editingPos.set(null);
    };

    addPosition = async () => {
        if (!this.guardEditPermission()) {
            return;
        }
        const electionId = this.selectedElectionId()!;
        const title = this.posFormTitle()?.trim();
        if (!title) {
            return;
        }
        try {
            await ElectionApi.createPosition(electionId, {
                positionTitle: title,
                descr: this.posFormDesc() || undefined,
                sortOrder: this.positionRows().length + 1,
            });
            this.newPositionMode.set(false);
            this.selectedElectionDetailGen.update((count) => count + 1);
        } catch (caughtError) {
            await AlertService.alert(S25Util.errorText(caughtError));
        }
    };

    updatePosition = async () => {
        if (!this.guardEditPermission()) {
            return;
        }
        const editing = this.editingPos();
        const electionId = this.selectedElectionId()!;
        if (!editing?.electionPositionId) {
            return;
        }
        try {
            await ElectionApi.updatePosition(electionId, editing.electionPositionId, {
                positionTitle: this.posFormTitle()?.trim() || editing.positionTitle,
                descr: this.posFormDesc() || editing.descr,
                sortOrder: editing.sortOrder,
            });
            this.editingPos.set(null);
            this.selectedElectionDetailGen.update((count) => count + 1);
        } catch (caughtError) {
            await AlertService.alert(S25Util.errorText(caughtError));
        }
    };

    removePosition = async (position: ElectionPositionDto) => {
        if (!this.guardEditPermission()) {
            return;
        }
        if (!position.electionPositionId) {
            return;
        }
        if (!(await AlertService.confirm("Delete this position?"))) {
            return;
        }
        try {
            await ElectionApi.deletePosition(position.electionPositionId);
            this.selectedElectionDetailGen.update((count) => count + 1);
        } catch (caughtError) {
            await AlertService.alert(S25Util.errorText(caughtError));
        }
    };

    addRule = async (electionPositionId: number) => {
        if (!this.guardEditPermission()) {
            return;
        }
        const electionId = this.selectedElectionId()!;
        const rowUi = this.addRuleUiByElectionPositionId()[electionPositionId];
        const voterRuleType = rowUi?.ruleType;
        if (!voterRuleType) {
            return;
        }
        const position = this.positionRows().find((row) => row.electionPositionId === electionPositionId);
        const existingRoleIds = new Set(
            (position?.voterRules ?? [])
                .filter((rule) => rule.ruleType === "ROLE" && rule.roleId != null)
                .map((rule) => rule.roleId!),
        );
        try {
            const createdRules: ElectionVoterRuleDto[] = [];
            if (voterRuleType === "ROLE") {
                const selectedRoles = rowUi?.roleDropDowns ?? [];
                const roleIds = selectedRoles
                    .map((roleItem) => Number(roleItem.itemId))
                    .filter((roleId) => roleId && Number.isFinite(roleId) && !existingRoleIds.has(roleId));
                if (!roleIds.length) {
                    await AlertService.alert("Choose at least one organization role that is not already added.");
                    return;
                }
                // this is an N+1 but it's very likely small so leaving a TODO here to fix later:
                for (const roleId of roleIds) {
                    const created = await ElectionApi.addVoterRule(electionId, electionPositionId, {
                        ruleType: voterRuleType,
                        roleId,
                    });
                    createdRules.push(created);
                }
            } else {
                const created = await ElectionApi.addVoterRule(electionId, electionPositionId, {
                    ruleType: voterRuleType,
                    roleId: null,
                });
                createdRules.push(created);
            }
            this.appendPositionVoterRules(electionPositionId, createdRules);
            this.addRuleUiByElectionPositionId.update((previous) => ({
                ...previous,
                [electionPositionId]: { ruleType: null, roleDropDowns: [] },
            }));
        } catch (caughtError) {
            await AlertService.alert(S25Util.errorText(caughtError));
        }
    };

    deleteRule = async (electionPositionVoterRuleId: number) => {
        if (!this.guardEditPermission()) {
            return;
        }
        try {
            await ElectionApi.deleteVoterRule(electionPositionVoterRuleId);
            this.removePositionVoterRule(electionPositionVoterRuleId);
        } catch (caughtError) {
            await AlertService.alert(S25Util.errorText(caughtError));
        }
    };

    deleteElectionFromListRow = async (election: ElectionDto) => {
        if (!this.canManageElectionsInstitutionWide()) {
            await AlertService.alert("You do not have permission to delete elections.");
            return;
        }
        const electionId = election.electionId;
        if (electionId == null) {
            return;
        }
        const titleFragment =
            election.name != null && election.name.trim() !== "" ? `the election "${election.name}"` : "this election";
        if (!(await this.deleteElectionWithConfirm(electionId, titleFragment))) {
            return;
        }
        this.listGen.update((count) => count + 1);
    };

    copyElectionFromListRow = async (electionId: number) => {
        if (!this.canManageElectionsInstitutionWide()) {
            await AlertService.alert("You do not have permission to copy elections.");
            return;
        }
        try {
            const copy = await ElectionApi.copyElection(electionId);
            this.listGen.update((count) => count + 1);
            if (copy.electionId != null) {
                this.openEdit(copy.electionId, "Election copied successfully.");
            }
        } catch (caughtError) {
            await AlertService.alert(S25Util.errorText(caughtError));
        }
    };

    deleteThisElection = async () => {
        if (this.isCreating()) {
            return;
        }
        if (!this.guardEditPermission()) {
            return;
        }
        const electionId = this.selectedElectionId();
        if (electionId == null) {
            return;
        }
        if (!(await this.deleteElectionWithConfirm(electionId, "this election"))) {
            return;
        }
        this.backToList();
        this.listGen.update((count) => count + 1);
    };

    private async deleteElectionWithConfirm(electionId: number, titleFragment: string): Promise<boolean> {
        if (
            !(await AlertService.confirm(
                `Are you sure you want to delete ${titleFragment}? It will no longer appear in lists and cannot be recovered.`,
            ))
        ) {
            return false;
        }
        try {
            await ElectionApi.deleteElection(electionId);
            return true;
        } catch (caughtError) {
            await AlertService.alert(S25Util.errorText(caughtError));
            return false;
        }
    }

    /** Patch voter rules in place so add/remove does not reload the full election (avoids form remount jump). */
    private appendPositionVoterRules(electionPositionId: number, addedRules: ElectionVoterRuleDto[]) {
        if (!addedRules.length) {
            return;
        }
        this.positionRows.update((positions) =>
            positions.map((position) =>
                position.electionPositionId === electionPositionId
                    ? { ...position, voterRules: [...(position.voterRules ?? []), ...addedRules] }
                    : position,
            ),
        );
    }

    private removePositionVoterRule(electionPositionVoterRuleId: number) {
        this.positionRows.update((positions) =>
            positions.map((position) => ({
                ...position,
                voterRules: (position.voterRules ?? []).filter(
                    (rule) => rule.electionPositionVoterRuleId !== electionPositionVoterRuleId,
                ),
            })),
        );
    }
}

/**
 * Backend stores election bounds as timezone-naive local date-time strings. Use the user's local
 * calendar date with start-of-day or end-of-day times (not `Date#toISOString()`, which shifts
 * clock time when converted to UTC).
 */
function formatElectionDayBoundaryForApi(selectedDate: Date, endOfDay: boolean): string {
    const padTwo = (unit: number) => String(unit).padStart(2, "0");
    const year = selectedDate.getFullYear();
    const month = padTwo(selectedDate.getMonth() + 1);
    const day = padTwo(selectedDate.getDate());
    const clock = endOfDay ? "23:59:59" : "00:00:00";
    return `${year}-${month}-${day}T${clock}`;
}

function normalizeElectionDtoDateTimes(election: ElectionDto): ElectionDto {
    const normalizedField = (raw: string, endOfDay: boolean): string => {
        const parsed = S25Util.date.parse(raw);
        return S25Util.date.isValid(parsed) ? formatElectionDayBoundaryForApi(parsed, endOfDay) : raw;
    };
    return {
        ...election,
        nominationStartDt: normalizedField(election.nominationStartDt, false),
        nominationEndDt: normalizedField(election.nominationEndDt, true),
        votingStartDt: normalizedField(election.votingStartDt, false),
        votingEndDt: normalizedField(election.votingEndDt, true),
    };
}

function blankElectionDraft(): ElectionDto {
    const today = new Date();
    return {
        name: "New Election",
        descr: "",
        nominationStartDt: formatElectionDayBoundaryForApi(today, false),
        nominationEndDt: formatElectionDayBoundaryForApi(today, true),
        votingStartDt: formatElectionDayBoundaryForApi(today, false),
        votingEndDt: formatElectionDayBoundaryForApi(today, true),
        candidateOrder: "SORT_ORDER",
        acctId: null,
        candidateIngestionPointId: null,
    };
}
